import { useNavigate, useLocation } from "react-router-dom";
import { ShallowEntity } from "./types";
import type { DatasetPanelKind } from "./datasetTabId";
import { createEntitySlug, createInteractionSlug } from "./utils/slugUtils";

// The dataset tab id lives in a module of its own, free of the router; it is
// named from here too, where the record page's other tab ids are.
export {
  datasetIdOfTab,
  datasetTab,
  type DatasetPanelKind,
} from "./datasetTabId";

export type PanelKind =
  | "dashboard"
  | "facts"
  | "terms"
  | "interactions"
  // The record's tasks (#11942 prototype): what concerns a manager or fund,
  // what is assigned to a person.
  | "tasks"
  | "documents"
  | "artifacts"
  | "holdings"
  | "performance"
  | "transactions"
  | "entities"
  | "contacts"
  | "activity"
  // One tab per dataset that has rows for the record (#12178). The only open
  // member: which datasets exist is data, so the id carries the dataset's. It
  // is the same string as the dataset's schema kind, one identity everywhere,
  // and a legal path segment, so the existing `:panelKind` route matches it.
  | DatasetPanelKind
  // A saved view a record page's layout opens as a tab of its own (#12998):
  // the base panel's grid on that view. The same `<kind>:<id>` shape.
  | ViewPanelKind;

export type ViewPanelKind = `view:${number}`;

const NATIVE_PANEL_KINDS: ReadonlySet<string> = new Set<PanelKind>([
  "dashboard",
  "facts",
  "terms",
  "interactions",
  "tasks",
  "documents",
  "artifacts",
  "holdings",
  "performance",
  "transactions",
  "entities",
  "contacts",
  "activity",
]);

/** Whether a tab id read from a layout (a plain string) is one of the record page's tabs. */
export const isPanelKind = (panel: string): panel is PanelKind =>
  NATIVE_PANEL_KINDS.has(panel) || /^(dataset|view):\d+$/.test(panel);

export const useEntityNavigate = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const isNotetakerRoute = location.pathname.startsWith("/notetaker");

  const prepareLink = (entity: ShallowEntity) => {
    // Safety check for entity with missing or invalid id
    if (!entity || !entity.id || !entity.name) {
      console.warn("Invalid entity passed to prepareLink:", entity);
      return `/rms/${entity?.kind || "unknown"}/${entity?.id || "unknown"}`;
    }
    // When on a notetaker route, link to notetaker filtered by entity
    if (isNotetakerRoute) {
      return `/notetaker?entityId=${encodeURIComponent(entity.id)}&entityName=${encodeURIComponent(entity.name)}`;
    }
    const slug = createEntitySlug(entity);
    return `/rms/${entity.kind}/${slug}`;
  };

  /**
   * A record tab's path. `tab` is the tab's address segment: a native or
   * dataset tab's key, or a view tab's name as `tabAddress` gives it
   * (#14666); the page resolves either back to the tab.
   */
  const prepareTab = (entity: ShallowEntity, tab: string, search?: string) => {
    return prepareLink(entity) + `/${tab}` + (search ?? "");
  };

  const prepareInteractionLink = (interaction: {
    id: string;
    title: string;
  }) => {
    // Safety check for interaction with missing or invalid id
    if (!interaction || !interaction.id || !interaction.title) {
      console.warn(
        "Invalid interaction passed to prepareInteractionLink:",
        interaction
      );
      return `/rms/interaction/${interaction?.id || "unknown"}`;
    }
    const slug = createInteractionSlug(interaction);
    return `/rms/interaction/${slug}`;
  };

  // Lateral moves replace, descents push (#11426, CD ruling on Ankit's
  // 2026-08-29 gap doc item 7). The tab strip is a set of peer views of one
  // record, so hopping between tabs replaces the history entry and never
  // builds a stack the user has to unwind. A card's "View all" is a descent
  // from a summary into the full list of the same content: it pushes, so
  // Back returns to where the user descended from.
  const navigateToTab = (
    entity: ShallowEntity,
    tab: string,
    search?: string,
    options: {
      descend?: boolean;
      /** Router state the tab reads on arrival (`interactionsArrival`, #14547). */
      state?: unknown;
    } = {}
  ) => {
    const newPath = prepareTab(entity, tab, search);
    const currentPath = location.pathname;

    // Check if we're navigating within the same entity (just changing tabs)
    const currentEntityPath = prepareLink(entity);
    const isTabChange = currentPath.startsWith(currentEntityPath);

    if (isTabChange && !options.descend) {
      // Use replace to avoid creating a new history entry for tab changes
      navigate(newPath, { replace: true, state: options.state });
    } else {
      // Normal navigation for different entities
      navigate(newPath, { state: options.state });
    }
  };

  const fn = (entity: ShallowEntity) => {
    navigate(prepareLink(entity));
  };

  fn.prepareLink = prepareLink;
  fn.prepareTab = prepareTab;
  fn.prepareInteractionLink = prepareInteractionLink;
  fn.isNotetakerRoute = isNotetakerRoute;
  fn.toInteraction = (interaction: { id: string; title: string }) =>
    navigate(prepareInteractionLink(interaction));
  fn.navigateToTab = navigateToTab;

  return fn;
};
